import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useEffect, useState } from 'react';

import {
  AccessEvent,
  AccessEventsDateRange,
  AccessEventsFilter,
  MobileOpCode,
  Query,
  QueryGetAccessEventsForTenantArgs
} from 'graphql/generated/graphqlTypes';

dayjs.extend(utc);

const getDateRange = (dateRange: AccessEventsDateRange) => ({
  rangeStart: dayjs(dateRange.rangeStart).second(0).millisecond(0).utc().format(),
  // subtract here one millisecond here so the date range interval is not closed
  rangeEnd: dayjs(dateRange.rangeEnd).second(0).millisecond(0).subtract(1, 'millisecond').utc().format()
});

const useExportAccessEvents = ({ handleFetchError }: BaseHookProps) => {
  const [filters, setFilters] = useState<null | AccessEventsFilter>(null);
  const [accessEvents, setAccessEvents] = useState<AccessEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);
  const [isDownloadDone, setIsDownloadDone] = useState<boolean>(false);

  const [getAccessEvents] = useLazyQuery<Pick<Query, 'getAccessEventsForTenant'>, QueryGetAccessEventsForTenantArgs>(
    GET_ACCESS_EVENTS_FOR_TENANT
  );

  useEffect(() => {
    if (isDownloadDone) {
      setIsDownloadInProgress(false);
    }
  }, [isDownloadDone]);

  useEffect(() => {
    if (!isDownloadDone && isDownloadInProgress) {
      const variables = filters
        ? {
            nextToken,
            filter: {
              ...filters,
              actions: filters.actions ? [...filters.actions] : [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
              dateRange: filters.dateRange ? getDateRange(filters.dateRange) : undefined
            }
          }
        : {
            nextToken,
            filter: {
              actions: [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted]
            }
          };

      (async () => {
        const { data, error } = await getAccessEvents({ variables });
        if (error) {
          handleFetchError('Error while fetching Access Events');
          return;
        }
        if (data) {
          if (data.getAccessEventsForTenant.nextToken !== nextToken)
            setAccessEvents([...accessEvents, ...data.getAccessEventsForTenant.items]);
          else setAccessEvents(data.getAccessEventsForTenant.items);
          setNextToken(data.getAccessEventsForTenant.nextToken || null);

          if (data.getAccessEventsForTenant.nextToken === null) setIsDownloadDone(true);
        }
      })();
    }
  }, [filters, getAccessEvents, isDownloadDone, isDownloadInProgress, nextToken]);

  useEffect(() => {
    if (isDownloadDone) {
      const headers = ['Date/Time', 'Visitor ID', 'User Name', 'User Email', 'Lock', 'Action', 'Reason'];

      const rows = accessEvents
        .map(
          (item) =>
            `${dayjs(item.createdAt).toISOString()},${item.visitor.id},"${item.visitor.name.replace(
              /"/g,
              '""'
            )}","${item.visitor.email.replace(/"/g, '""')}","${item.door.name.replace(
              /"/g,
              '""'
            )}","${item.action.replace(/"/g, '""')}","${item.reason || ''.replace(/"/g, '""')}"`
        )
        .join('\n');

      const data = `${headers}\n${rows}`;
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `accessEventsExport ${dayjs().format('YYYY-MM-DD HH-mm')}`);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }, [accessEvents, isDownloadDone]);

  const startDownloadingAccessEventsToFile = (filter: null | AccessEventsFilter) => {
    setFilters(filter);
    if (!isDownloadInProgress) setIsDownloadInProgress(true);
  };

  const abortDownloadingAccessEventsToFile = () => {
    setAccessEvents([]);
    setIsDownloadInProgress(false);
    setIsDownloadDone(false);
  };

  return {
    startDownloadingAccessEventsToFile,
    abortDownloadingAccessEventsToFile,
    isDownloadInProgress,
    isDownloadDone
  };
};

export default useExportAccessEvents;
